import type { AddItemDto } from '@azoupvendas/shared';

export type AutosavePayload = { pedidoId: string; itens: AddItemDto[] };
export type AutosaveStatus = 'saving' | 'saved' | 'error';

// AIDEV-NOTE: Finding 2 (final review) — tipo/helper compartilhado para erros definitivos do
// autosave (nao vale a pena retentar). Antes so 413 usava esse padrao (montado ad-hoc no wizard
// com Object.assign); extraido aqui para o wizard tambem marcar 400 (INVALID_STATUS_FOR_AUTOSAVE —
// pedido nao esta mais em rascunho, ex.: draft vazado de edicao) e 403 (RBAC — vendedor sem acesso
// ao cliente/pedido) como naoRetryavel, evitando retry infinito com Finalizar bloqueado para sempre.
// 404 fica FORA de proposito: e um estado transitorio legitimo (rascunho criado offline, ainda nao
// drenado do op_log pro server) e deve continuar retryavel ate o rascunho existir no server.
export interface ErroNaoRetryavel extends Error {
  naoRetryavel: true;
}

export function marcarNaoRetryavel(err: unknown): never {
  throw Object.assign(err as Error, { naoRetryavel: true as const });
}

// AIDEV-NOTE: Fila serializada de autosave do rascunho — garante no maximo 1 PATCH em voo
// e que o payload aplicado por ultimo no server e sempre o mais recente do client.
// Sem isso, um PATCH pendurado (timeout 30s) podia chegar DEPOIS de um mais novo e
// sobrescrever o rascunho com estado antigo (replace-all no server).
export class AutosaveQueue {
  private latest: AutosavePayload | null = null;
  private inFlight = false;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(
    private readonly send: (p: AutosavePayload) => Promise<void>,
    private readonly onStatus: (s: AutosaveStatus) => void
  ) {}

  push(payload: AutosavePayload): void {
    this.latest = payload;
    this.attempt = 0;
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    void this.flush();
  }

  private async flush(): Promise<void> {
    if (this.inFlight || !this.latest || this.disposed) return;
    const payload = this.latest;
    this.inFlight = true;
    this.onStatus('saving');
    try {
      await this.send(payload);
      this.inFlight = false;
      // AIDEV-NOTE: reverifica disposed apos o await — dispose() pode ter sido chamado
      // com o request em voo; nesse caso nao atualiza status nem reagenda nada.
      if (this.disposed) return;
      if (this.latest !== payload) {
        // chegou payload mais novo durante o envio — envia ja
        void this.flush();
        return;
      }
      this.latest = null;
      this.onStatus('saved');
    } catch (e) {
      this.inFlight = false;
      // AIDEV-NOTE: idem — se dispose() ocorreu durante o envio que falhou, nao chama
      // onStatus (componente pode estar desmontado) nem agenda retry (dispose ja limpou).
      if (this.disposed) return;
      // AIDEV-NOTE: erro naoRetryavel (ex: 413 — payload excede o limite do server) nunca vai
      // caber num retry; reagendar so queimaria rede/bateria sem chance de sucesso. Descarta
      // o pending e sinaliza erro definitivo (status 'error' fica visivel, Finalizar bloqueado).
      if ((e as Partial<ErroNaoRetryavel>).naoRetryavel) {
        this.latest = null;
        this.onStatus('error');
        return;
      }
      this.onStatus('error');
      // AIDEV-NOTE: retry infinito com backoff 1s→2s→4s... cap 30s enquanto o wizard viver;
      // o payload persiste no store (localStorage), entao fechar a aba nao perde nada.
      const delay = Math.min(1000 * 2 ** this.attempt, 30_000);
      this.attempt += 1;
      this.retryTimer = setTimeout(() => void this.flush(), delay);
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
  }
}
